import { caseDbApiEndpoint } from '../utils/apiOrigin';

const DOCUMENT_REVISION_PATTERN = /^[a-f0-9]{64}$/i;
const MAX_LOGICAL_PATH_LENGTH = 2048;
export const DOCUMENT_LIVE_REVISION_TIMEOUT_MS = 15000;
export const DOCUMENT_LIVE_UNSUPPORTED_ERROR_CODE = 'DOCUMENT_LIVE_UNSUPPORTED';

const documentLiveResponseError = (response, message) => {
    const error = new Error(message || 'Document updates could not be checked.');
    const status = Number(response?.status || 0);
    if (status > 0) {
        error.status = status;
    }
    if ([404, 405, 410, 501].includes(status)) {
        error.name = 'DocumentLiveUnsupportedError';
        error.code = DOCUMENT_LIVE_UNSUPPORTED_ERROR_CODE;
    }
    return error;
};

const containsControlCharacter = (value) => Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0);
    return codePoint <= 0x1f || codePoint === 0x7f;
});

export const normalizeDocumentLiveRevisionPath = (rawPath = '{{Name}}') => {
    if (rawPath !== null && typeof rawPath !== 'string') {
        throw new Error('A valid document folder path is required to check for updates.');
    }

    let path = String(rawPath || '');
    if (!path || path === 'root') return '{{Name}}';
    if (path.length > MAX_LOGICAL_PATH_LENGTH || containsControlCharacter(path)) {
        throw new Error('A valid document folder path is required to check for updates.');
    }

    path = path.replace(/\\/g, '/').replace(/\/+$/g, '');
    if (!path || path.startsWith('/') || /^cases\/[^/]+\//i.test(path)) {
        throw new Error('A valid document folder path is required to check for updates.');
    }
    if (!path.startsWith('{{Name}}')) {
        path = `{{Name}}/${path}`;
    }

    const segments = path.split('/');
    if (
        segments.length > 100
        || segments[0] !== '{{Name}}'
        || segments.some((segment, index) => (
            !segment || segment === '.' || segment === '..' || (index > 0 && segment === '{{Name}}')
        ))
    ) {
        throw new Error('A valid document folder path is required to check for updates.');
    }

    return segments.join('/');
};

const nonNegativeInteger = (value, fallback = 0) => {
    const numeric = Number(value);
    return Number.isInteger(numeric) && numeric >= 0 ? numeric : fallback;
};

const abortError = () => {
    const error = new Error('The document update check was cancelled.');
    error.name = 'AbortError';
    return error;
};

const timeoutError = () => {
    const error = new Error('The document update check timed out.');
    error.name = 'TimeoutError';
    return error;
};

export const requestDocumentLiveRevision = async ({
    caseId,
    path = '{{Name}}',
    knownRevision = null,
    fetchImpl = fetch,
    endpoint,
    signal,
    timeoutMs = DOCUMENT_LIVE_REVISION_TIMEOUT_MS
} = {}) => {
    const normalizedCaseId = Number(caseId);
    if (!Number.isInteger(normalizedCaseId) || normalizedCaseId <= 0) {
        throw new Error('A valid case is required to check for document updates.');
    }
    if (typeof fetchImpl !== 'function') {
        throw new Error('Document updates could not be checked.');
    }

    const normalizedTimeoutMs = Number(timeoutMs);
    if (!Number.isFinite(normalizedTimeoutMs) || normalizedTimeoutMs <= 0) {
        throw new Error('A valid document update timeout is required.');
    }

    const normalizedPath = normalizeDocumentLiveRevisionPath(path);
    const normalizedKnownRevision = knownRevision == null
        ? ''
        : String(knownRevision).trim().toLowerCase();
    if (normalizedKnownRevision && !DOCUMENT_REVISION_PATTERN.test(normalizedKnownRevision)) {
        throw new Error('The known document revision is invalid.');
    }

    const url = new URL(endpoint || caseDbApiEndpoint('document-live-revision.php'), (
        typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    ));
    url.searchParams.set('case_id', String(normalizedCaseId));
    url.searchParams.set('path', normalizedPath);
    if (normalizedKnownRevision) {
        url.searchParams.set('known_revision', normalizedKnownRevision);
    }

    const controller = new AbortController();
    if (signal?.aborted) {
        throw abortError();
    }
    let cancelRequest;
    const cancellation = new Promise((_, reject) => {
        cancelRequest = reject;
    });
    const handleExternalAbort = () => {
        cancelRequest(abortError());
        controller.abort();
    };
    signal?.addEventListener?.('abort', handleExternalAbort, { once: true });
    const timeoutId = setTimeout(() => {
        cancelRequest(timeoutError());
        controller.abort();
    }, normalizedTimeoutMs);

    let response;
    try {
        response = await Promise.race([
            fetchImpl(url.toString(), {
                method: 'GET',
                credentials: 'include',
                cache: 'no-store',
                headers: { Accept: 'application/json' },
                signal: controller.signal
            }),
            cancellation
        ]);

        let payload = null;
        try {
            payload = await Promise.race([response.json(), cancellation]);
        } catch (error) {
            if (error?.name === 'AbortError' || error?.name === 'TimeoutError') {
                throw error;
            }
            payload = null;
        }

        if (!response.ok) {
            throw documentLiveResponseError(
                response,
                payload?.message || 'Document updates could not be checked.'
            );
        }

        const revision = String(payload?.revision || '').toLowerCase();
        const hasCompleteness = typeof payload?.complete === 'boolean'
            || typeof payload?.truncated === 'boolean';
        if (
            !payload?.success
            || !DOCUMENT_REVISION_PATTERN.test(revision)
            || typeof payload?.path !== 'string'
            || !hasCompleteness
        ) {
            throw new Error(payload?.message || 'Document updates could not be checked.');
        }

        const responsePath = normalizeDocumentLiveRevisionPath(payload.path);
        if (responsePath !== normalizedPath) {
            throw new Error('Document updates could not be checked.');
        }
        const complete = typeof payload.complete === 'boolean'
            ? payload.complete
            : payload.truncated !== true;
        const truncated = typeof payload.truncated === 'boolean'
            ? payload.truncated
            : !complete;

        // A contradictory completeness pair is not safe to cache. Treat it as a
        // malformed success instead of allowing a partial inventory to suppress a
        // document refresh.
        if (complete === truncated) {
            throw new Error('Document updates could not be checked.');
        }

        return {
            revision,
            changed: typeof payload.changed === 'boolean'
                ? payload.changed
                : !normalizedKnownRevision || normalizedKnownRevision !== revision,
            path: responsePath,
            itemCount: nonNegativeInteger(payload.item_count),
            objectCount: nonNegativeInteger(payload.object_count),
            folderCount: nonNegativeInteger(payload.folder_count),
            complete,
            // Kept explicit for callers that were built around the initial
            // prototype contract. `complete` is the canonical field.
            truncated,
            scannedPages: Math.max(1, nonNegativeInteger(payload.scanned_pages, 1)),
            updatedAt: typeof payload.updated_at === 'string' && payload.updated_at
                ? payload.updated_at
                : null
        };
    } finally {
        clearTimeout(timeoutId);
        signal?.removeEventListener?.('abort', handleExternalAbort);
    }
};
